import { useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { SLIDER_KEYS, sliderKeyValue } from './appearance-controls.util';

export interface ValueControlOptions {
  value: number;
  /** A value to preview. */
  onChange: (value: number) => void;
  /** Saves the last value previewed. */
  onCommit: () => void;
  /** Drops the values previewed since the last save. */
  onCancel: () => void;
  /** The value at a pointer, from the control's box. */
  valueAt: (event: PointerEvent<HTMLElement>, rect: DOMRect) => number;
}

/**
 * Pointer and keyboard handling for a 0 to 100 control: a press or drag
 * previews and the release saves; a key previews as it goes down and saves
 * as it comes up, so a held arrow is one save; Escape drops what was
 * previewed.
 */
export function useValueControl({
  value,
  onChange,
  onCommit,
  onCancel,
  valueAt,
}: ValueControlOptions) {
  const pointer = useRef<number | null>(null);
  const keyMoved = useRef(false);

  const at = (event: PointerEvent<HTMLElement>) =>
    Math.round(valueAt(event, event.currentTarget.getBoundingClientRect()));

  return {
    onPointerDown(event: PointerEvent<HTMLElement>) {
      if (event.button !== 0 || pointer.current !== null) return;
      event.preventDefault();
      event.currentTarget.focus();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      pointer.current = event.pointerId;
      onChange(at(event));
    },
    onPointerMove(event: PointerEvent<HTMLElement>) {
      if (pointer.current !== event.pointerId) return;
      onChange(at(event));
    },
    onPointerUp(event: PointerEvent<HTMLElement>) {
      if (pointer.current !== event.pointerId) return;
      pointer.current = null;
      if (event.currentTarget.hasPointerCapture?.(event.pointerId))
        event.currentTarget.releasePointerCapture(event.pointerId);
      onCommit();
    },
    onPointerCancel(event: PointerEvent<HTMLElement>) {
      if (pointer.current !== event.pointerId) return;
      pointer.current = null;
      onCancel();
    },
    onKeyDown(event: KeyboardEvent<HTMLElement>) {
      if (event.key === 'Escape') {
        if (pointer.current === null && !keyMoved.current) return;
        event.preventDefault();
        event.stopPropagation();
        pointer.current = null;
        keyMoved.current = false;
        onCancel();
        return;
      }
      const next = sliderKeyValue(event.key, event.shiftKey, value);
      if (next === null) return;
      event.preventDefault();
      keyMoved.current = true;
      onChange(next);
    },
    onKeyUp(event: KeyboardEvent<HTMLElement>) {
      if (!SLIDER_KEYS.has(event.key) || !keyMoved.current) return;
      keyMoved.current = false;
      onCommit();
    },
  };
}
